import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeIn } from "@/components/Reveal";
import { ARTICLES } from "@/data/articles";
import { useLanguage } from "@/i18n/LanguageContext";

export default function InsightArticle() {
  const { slug } = useParams();
  const { t } = useLanguage();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-40 text-center">
        <p className="text-slate-500">Article not found.</p>
        <Link to="/insights" className="mt-4 inline-block text-sm font-semibold text-amber-700">← {t("in.back")}</Link>
      </div>
    );
  }

  return (
    <div data-testid="insight-article-page">
      <Seo
        title={`${article.title} | Wisdom Insurance Broking`}
        description={article.excerpt}
        keywords={`${article.category} insurance India, commercial insurance broker Pune`}
      />
      <article className="mx-auto max-w-3xl px-4 pb-20 pt-32 sm:px-8">
        <FadeIn>
          <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-amber-700">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("in.back")}
          </Link>
          <div className="mt-8 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
            <span className="text-amber-700">{article.category}</span>
            <span>{new Date(article.date).toLocaleDateString("en-IN", { dateStyle: "long" })}</span>
            <span>{article.readTime} {t("in.minRead")}</span>
          </div>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl">{article.title}</h1>
        </FadeIn>

        <FadeIn delay={0.1} className="mt-10 space-y-6">
          {article.content.map((block, i) => {
            if (block.type === "h2") {
              return <h2 key={i} className="pt-4 font-heading text-xl font-semibold text-navy">{block.text}</h2>;
            }
            if (block.type === "ul") {
              return (
                <ul key={i} className="space-y-2.5">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-base leading-relaxed text-slate-700">
                      <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-amber-600" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              );
            }
            return <p key={i} className="text-base leading-relaxed text-slate-700">{block.text}</p>;
          })}
        </FadeIn>

        <FadeIn className="mt-14 border border-slate-200 bg-navy p-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{t("in.related")}</p>
          <p className="mt-3 font-heading text-lg font-semibold text-white">{article.category}</p>
          <Link
            to={`/contact?type=${encodeURIComponent(article.ctaType || article.category)}`}
            data-testid="article-cta"
            className="group mt-5 inline-flex items-center gap-2 bg-amber-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-500"
          >
            {t("in.cta")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeIn>
      </article>
    </div>
  );
}
